import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/hero";
import { getFilmById } from "@/utils/film";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = heroSlides.length;

  const goTo = useCallback(
    (next: number) => {
      setIndex((next + total) % total);
    },
    [total]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5500);
    return () => clearInterval(timer);
  }, [total]);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      goTo(diff < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative h-[420px] w-full overflow-hidden md:h-[560px] lg:h-[620px]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {heroSlides.map((slide, slideIndex) => {
        const film = getFilmById(slide.filmId);
        return (
          <div
            key={slide.filmId}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              slideIndex === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={slide.backdrop}
              alt={`${slide.headline}焦点图`}
              title={`${slide.headline} 妖精影视推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/45 to-transparent" />
          </div>
        );
      })}

      <div className="relative z-10 flex h-full w-full items-center">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-8">
          <div key={index} className="ff-animate-up max-w-xl">
            <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-primary-950">
              <i className="ri-sparkling-2-fill" />
              本周焦点
            </span>
            <h2 className="font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              {heroSlides[index].headline}
            </h2>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-background-200 md:mt-4 md:text-base">
              {heroSlides[index].sub}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3 md:mt-8">
              <Link
                to={`/detail/${heroSlides[index].filmId}`}
                className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                查看详情
              </Link>
              <Link
                to={
                  getFilmById(heroSlides[index].filmId)?.type === "电影"
                    ? "/category/电影"
                    : "/category/电视剧"
                }
                className="whitespace-nowrap rounded-md border border-background-100/50 bg-background-50/10 px-5 py-2.5 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
              >
                浏览同类作品
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {heroSlides.map((slide, slideIndex) => (
          <button
            key={slide.filmId}
            type="button"
            aria-label={`切换到第 ${slideIndex + 1} 张焦点图`}
            onClick={() => setIndex(slideIndex)}
            className={`h-2 rounded-full transition-all ${
              slideIndex === index ? "w-7 bg-accent-500" : "w-2 bg-background-50/60"
            }`}
          />
        ))}
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(index - 1)}
        className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 md:left-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(index + 1)}
        className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 md:right-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>
    </section>
  );
}